/**
 * The loremap brand: one mark, drawn once, used everywhere it appears (the
 * sidebar masthead, the sign-in card). The favicon and app icons in
 * web/public repeat this geometry by hand, because static files cannot
 * import it; scripts/gen_brand_icons.sh rasterises them from favicon.svg,
 * and brandAssets.test.ts holds the two copies to the same path.
 *
 * The mark is a route across a map: three nodes joined in an L (the
 * product's initial, and the shape of a flow read top to bottom then left
 * to right) plus one node still unjoined, top right: the part of the system
 * you have not been walked through yet. It draws in currentColor, so the
 * caller picks the ink (the accent in the chrome, the canvas colour on an
 * accent tile), and every proportion sits on a 24-unit grid so it survives
 * 16px.
 */

import type { CSSProperties } from 'react';

/** The route the mark draws; favicon.svg carries the same string. */
export const MARK_ROUTE = 'M6.5 6.5V17.5H17.5';

/** The glyph alone, sized in px, inheriting its colour. */
export function LoremapMark({
  size = 24,
  className,
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      className={className}
      style={style}
      data-testid="loremap-mark"
    >
      <path
        d={MARK_ROUTE}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="6.5" cy="6.5" r="2.7" fill="currentColor" />
      <circle cx="6.5" cy="17.5" r="2.7" fill="currentColor" />
      <circle cx="17.5" cy="17.5" r="2.7" fill="currentColor" />
      <circle cx="17.5" cy="6.5" r="2.3" fill="none" stroke="currentColor" strokeWidth="2.1" />
    </svg>
  );
}

/**
 * The mark beside the name. `hideWordmark` keeps the mark alone (the
 * collapsed rail); the accessible name stays "loremap" either way.
 */
export function LoremapLogo({
  size = 22,
  hideWordmark = false,
  className,
}: {
  size?: number;
  hideWordmark?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-2 ${className ?? ''}`}
      data-testid="loremap-logo"
    >
      <LoremapMark size={size} className="shrink-0 text-(--accent)" />
      {hideWordmark ? (
        <span className="sr-only">loremap</span>
      ) : (
        <span
          className="text-[14px] leading-none font-semibold tracking-[-0.01em] text-primary"
          data-testid="loremap-wordmark"
        >
          loremap
        </span>
      )}
    </span>
  );
}
